import React, { useState, useRef } from 'react';
import { Upload, X, Video, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { tutorService } from '../services/tutorService';
import { toast } from 'sonner';

const VideoUpload = ({ value, onChange, maxSizeMB = 15 }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [videoPreview, setVideoPreview] = useState(value || null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['video/mp4', 'video/mov', 'video/avi', 'video/x-msvideo', 'video/quicktime'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a valid video file (MP4, MOV, or AVI)');
      return;
    }

    // Validate file size (15MB = 15 * 1024 * 1024 bytes)
    const maxSize = maxSizeMB * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error(`Video size must be less than ${maxSizeMB}MB`);
      return;
    }

    // Create preview
    const previewUrl = URL.createObjectURL(file);
    setVideoPreview(previewUrl);

    // Upload video
    await uploadVideo(file);
  };

  const uploadVideo = async (file) => {
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append('video', file);

      // Simulate progress (since we don't have real progress tracking)
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 200);

      const response = await tutorService.uploadDemoVideo(formData);

      clearInterval(progressInterval);
      setUploadProgress(100);

      if (response.success) {
        toast.success('Video uploaded successfully!');
        onChange(response.data.path);
      }
    } catch (error) {
      console.error('Video upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload video');
      setVideoPreview(null);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemove = async () => {
    if (!value) {
      setVideoPreview(null);
      return;
    }

    try {
      await tutorService.deleteDemoVideo(value);
      setVideoPreview(null);
      onChange(null);
      toast.success('Video removed successfully');
    } catch (error) {
      console.error('Video delete error:', error);
      toast.error('Failed to remove video');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="space-y-4">
      {!videoPreview ? (
        <div
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-all duration-200"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="video/mp4,video/mov,video/avi,video/quicktime,video/x-msvideo"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading}
          />
          
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
              <Video className="h-8 w-8 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Upload Demo Video
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Click to browse or drag and drop your video here
            </p>
            <p className="text-xs text-gray-500">
              MP4, MOV, or AVI (max {maxSizeMB}MB)
            </p>
          </div>
        </div>
      ) : (
        <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
          {/* Video Preview */}
          <div className="relative bg-black">
            <video
              ref={videoRef}
              src={videoPreview}
              controls
              className="w-full max-h-96"
            >
              Your browser does not support the video tag.
            </video>
            
            {/* Remove Button */}
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-2 hover:bg-red-700 transition-colors"
              disabled={uploading}
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="p-4 bg-blue-50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-blue-900">Uploading...</span>
                <span className="text-sm font-medium text-blue-900">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Success Message */}
          {!uploading && value && (
            <div className="p-4 bg-green-50 border-t border-green-200">
              <div className="flex items-center text-green-800">
                <CheckCircle className="h-5 w-5 mr-2" />
                <span className="text-sm font-medium">Video uploaded successfully!</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Guidelines */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-1">Video Guidelines:</p>
            <ul className="list-disc list-inside space-y-1 text-xs">
              <li>Show your teaching style and methodology</li>
              <li>Keep it between 2-5 minutes</li>
              <li>Ensure good lighting and clear audio</li>
              <li>Demonstrate your skill with examples</li>
              <li>Be professional and engaging</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VideoUpload;
